/**
 * PrivateOffersCard — shown on the user dashboard when the current
 * user has one or more private offers granted to them by a Super
 * Admin. Renders each offer with plan/discount/expiry + an activate
 * CTA. Auto-hides if the user has no offers.
 *
 * API:
 *   GET  /api/me/private-offers               → list mine
 *   POST /api/me/private-offers/{id}/activate → activate
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2, Sparkles, Clock } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";

const PLAN_AR = {
  individual_plus: "الأفراد بلس",
  dealer_pro: "التاجر برو",
  enterprise: "المؤسسات",
};
const PLAN_EN = {
  individual_plus: "Individual Plus",
  dealer_pro: "Dealer Pro",
  enterprise: "Enterprise",
};

function daysLeft(endAt) {
  if (!endAt) return null;
  const diff = new Date(endAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function PrivateOffersCard() {
  const { lang } = useI18n();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/me/private-offers");
      setOffers(data.items || []);
    } catch (e) {
      // Silently ignore — user might not be authenticated during first render
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activate = async (o) => {
    setActivatingId(o.id);
    try {
      await apiClient.post(`/me/private-offers/${o.id}/activate`);
      toast.success(
        lang === "ar"
          ? "تم تفعيل العرض! تم ترقية اشتراكك."
          : "Offer activated! Your subscription has been upgraded."
      );
      // Refresh both the offers list and the parent's auth context
      // (dashboard reads user.subscription_tier — a full reload picks it up
      // and re-runs the tier-aware ribbon/widgets).
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
        (lang === "ar" ? "تعذّر تفعيل العرض" : "Failed to activate offer")
      );
    } finally {
      setActivatingId(null);
    }
  };

  if (loading) return null;
  // Only surface offers that are still relevant (active + not yet
  // activated + not expired) OR the currently-active grant so the user
  // sees the remaining time.
  const now = Date.now();
  const visible = offers.filter((o) => {
    if (o.reverted_at) return false;
    const end = o.end_at ? new Date(o.end_at).getTime() : null;
    if (end && end < now) return false;
    if (o.activated_at) return true; // show as active
    return !!o.active;
  });
  if (visible.length === 0) return null;

  return (
    <div
      className="rounded-2xl p-5 sm:p-6 mb-6 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0a0a0a 0%, #1f1a12 60%, #2a2010 100%)",
        border: "1px solid rgba(201,165,92,0.30)",
        color: "#f5f0e2",
      }}
      data-testid="private-offers-card"
    >
      <Sparkles
        className="w-24 h-24 absolute -top-4 -end-4 opacity-[0.06] text-[#c9a55c]"
        aria-hidden
      />
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-5 h-5 text-[#c9a55c]" />
        <div className="overline overline-gold" style={{ color: "#c9a55c" }}>
          {lang === "ar" ? "عرض خاص مُقدَّم لك" : "A private offer for you"}
        </div>
      </div>

      <div className="space-y-3">
        {visible.map((o) => {
          const activated = !!o.activated_at;
          const dl = daysLeft(o.end_at);
          return (
            <div
              key={o.id}
              className="rounded-xl p-4 flex items-center gap-4 flex-wrap"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,165,92,0.20)" }}
              data-testid={`my-offer-${o.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="font-display text-lg" style={{ color: "#f5f0e2" }}>
                  {lang === "ar" ? PLAN_AR[o.plan] : PLAN_EN[o.plan]}
                  {" · "}
                  <span style={{ color: "#c9a55c" }}>
                    {lang === "ar" ? `خصم ${o.discount_pct}٪` : `${o.discount_pct}% off`}
                  </span>
                </div>
                <div className="text-[12px] mt-1 flex items-center gap-2" style={{ color: "#c9a55c99" }}>
                  <Clock className="w-3.5 h-3.5" />
                  {activated
                    ? (lang === "ar"
                        ? `مفعّل — يتبقى ${dl} يوماً`
                        : `Active — ${dl} day${dl === 1 ? "" : "s"} left`)
                    : (lang === "ar"
                        ? `صالح حتى ${o.end_at ? o.end_at.slice(0, 10) : "—"} (${dl} يوم)`
                        : `Valid until ${o.end_at ? o.end_at.slice(0, 10) : "—"} (${dl} days)`)}
                </div>
              </div>

              {activated ? (
                <div
                  className="flex items-center gap-1 text-[13px] px-3 py-2 rounded-lg"
                  style={{ background: "rgba(15,124,58,0.15)", color: "#4ade80" }}
                  data-testid={`offer-active-${o.id}`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {lang === "ar" ? "مفعّل" : "Activated"}
                </div>
              ) : (
                <button
                  onClick={() => activate(o)}
                  disabled={activatingId === o.id}
                  className="text-[13px] font-bold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60"
                  style={{ background: "#c9a55c", color: "#0a0a0a" }}
                  data-testid={`activate-offer-${o.id}`}
                >
                  {activatingId === o.id
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Gift className="w-4 h-4" />}
                  {lang === "ar" ? "تفعيل العرض" : "Activate offer"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] mt-4" style={{ color: "#c9a55c99" }}>
        {lang === "ar"
          ? "بعد انتهاء العرض، سيعود حسابك تلقائياً إلى الباقة السابقة."
          : "When this offer expires, your account will automatically return to the previous plan."}
      </p>
    </div>
  );
}
